import React from 'react';
import { useNavigate } from 'react-router-dom';

const NAVY = '#112030';
const GREEN = '#4CAF50';
const eyebrow = { fontSize: 12, fontWeight: 800, letterSpacing: '.13em', color: GREEN, marginBottom: 14 };
const card = { background: '#fff', border: '1px solid #e5eaf0', borderRadius: 14, boxShadow: '0 1px 3px rgba(16,42,67,.06)' };

const FEATURES = [
  { title: 'Highly Targeted Criteria', body: 'Filter by demographics, location, party, voting history, age range, and more. Reach exactly who you need.', iconBg: '#f0fdf4', iconColor: '#2e7d32', path: 'M22 3H2l8 9.46V19l4 2v-8.54z' },
  { title: 'Up-to-Date Data', body: 'Sourced directly from state voter files and updated regularly for accuracy and freshness.', iconBg: '#eaf0ff', iconColor: '#1565c0', path: 'M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15' },
  { title: 'Flexible Export', body: 'Download lists in CSV, ready for mail houses. Compatible with all major direct mail services.', iconBg: '#f6edfb', iconColor: '#7b1fa2', path: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3' },
  { title: 'Privacy Compliant', body: 'All data is sourced from public voter registration records and complies with state and federal law.', iconBg: '#fff3e6', iconColor: '#e65100', path: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' },
];
const STEPS = [
  { n: '1', title: 'Select Your State', body: 'Choose from FL, NC, PA, OH, OK, or AR.' },
  { n: '2', title: 'Define Your Criteria', body: 'Use the Query Wizard to filter by geography, party, age, and more.' },
  { n: '3', title: 'Preview & Purchase', body: 'See your results instantly and download your list.' },
];
const USE_CASES = [
  { title: 'Political Campaigns', body: 'Target likely voters, swing voters, or specific party affiliations for your mailers.' },
  { title: 'Advocacy Groups', body: 'Reach engaged citizens likely to support your cause based on voting history.' },
  { title: 'Get Out The Vote', body: 'Identify registered voters who haven’t voted recently and encourage them to participate.' },
  { title: 'Fundraising', body: 'Target donors based on past engagement and demographic profiles.' },
];

function MailingListsPage() {
  const navigate = useNavigate();
  const goVoters = () => navigate('/voters');

  return (
    <div id="mailingListsContent" className="content-section" style={{ background: '#eef2f6', boxShadow: 'none', padding: 0, borderRadius: 0 }}>
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '36px 28px 72px' }}>

        {/* Brand header */}
        <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 16, padding: '20px 24px', marginBottom: 24 }}>
          <img src="/doornoc-logo.png" alt="Doornoc" style={{ width: 52, height: 52, objectFit: 'contain', flexShrink: 0 }} />
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900, color: NAVY }}>Custom Voter Mailing Lists</h1>
            <div style={{ fontSize: 13, color: '#94a3b8' }}>Powered by <b style={{ color: '#475569' }}>Doornoc</b></div>
          </div>
        </div>

        <p style={{ fontSize: 17, lineHeight: 1.7, color: '#475569', maxWidth: 760, margin: '0 0 32px' }}>Send the right mail, to the right people, at the right time. Our custom mailing lists are built from comprehensive voter data across multiple states — giving your campaign the precision targeting it needs to succeed.</p>

        {/* Features */}
        <div style={eyebrow}>WHAT MAKES OUR LISTS DIFFERENT?</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px,1fr))', gap: 16, marginBottom: 36 }}>
          {FEATURES.map(f => (
            <div key={f.title} style={{ ...card, padding: 20 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: f.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke={f.iconColor} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d={f.path} /></svg>
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 6 }}>{f.title}</div>
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: '#475569' }}>{f.body}</p>
            </div>
          ))}
        </div>

        {/* How it works */}
        <div style={{ background: 'linear-gradient(160deg,#1b2c3f,#0a1420)', borderRadius: 16, padding: 32, color: '#fff', marginBottom: 36 }}>
          <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.1em', color: '#7fe08a', marginBottom: 20 }}>HOW IT WORKS</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))', gap: 24 }}>
            {STEPS.map(s => (
              <div key={s.n}>
                <div style={{ width: 42, height: 42, borderRadius: 12, background: GREEN, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 18, marginBottom: 12 }}>{s.n}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#fff', marginBottom: 5 }}>{s.title}</div>
                <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: 'rgba(255,255,255,.7)' }}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Use cases */}
        <div style={eyebrow}>PERFECT FOR</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px,1fr))', gap: 16, marginBottom: 40 }}>
          {USE_CASES.map(u => (
            <div key={u.title} style={{ ...card, padding: 20 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 6 }}>{u.title}</div>
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: '#475569' }}>{u.body}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div style={{ background: NAVY, borderRadius: 16, padding: 40, textAlign: 'center', color: '#fff' }}>
          <h3 style={{ margin: '0 0 10px', fontSize: 24, fontWeight: 900, color: '#fff' }}>Ready to build your mailing list?</h3>
          <p style={{ margin: '0 0 22px', fontSize: 16, color: 'rgba(255,255,255,.75)' }}>Start with our Query Wizard and create your targeted voter list in minutes.</p>
          <button onClick={goVoters} style={{ border: 'none', cursor: 'pointer', background: GREEN, color: '#fff', fontSize: 16, fontWeight: 700, borderRadius: 11, padding: '14px 34px', boxShadow: '0 4px 18px rgba(76,175,80,.4)' }}>Start building your list</button>
          <p style={{ margin: '16px 0 0', fontSize: 13, color: 'rgba(255,255,255,.5)' }}>Questions? Email <b style={{ color: '#fff' }}>contact@doornoc.com</b></p>
        </div>
      </div>
    </div>
  );
}

export default MailingListsPage;
